import { readChartEligibility, requireChartOfflineEligibility } from './chartEligibility';
import config from '../config';
import { getOfflineOwnerIdentity } from './offlineIdentity';
import { cleanupPreparedCharts, getPreparedChartManifest, readPreparedResource, setPreparedChartPin, storePreparedChartResources } from './preparedChartStore';
import { getVaultStatus, isOfflineVaultUnlocked, lockOfflineVault } from './vault';

export { getOfflineOwnerIdentity } from './offlineIdentity';
const STORAGE_KEY = 'senaOfflineSessionGrant';
const CONTINUITY_KEY = 'senaActiveLaborContinuities';

function decode(token) {
  try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); }
  catch { return null; }
}

export function getOfflineSession() {
  const token = localStorage.getItem(STORAGE_KEY);
  const grant = token ? decode(token) : null;
  const owner = getOfflineOwnerIdentity();
  if (!owner || !grant || grant.offlineCharting !== 'true' || !grant.exp || grant.sub !== owner.userReferenceId) return null;
  const context = grant.contextReferenceId || grant.organizationReferenceId;
  if (String(context || '') !== owner.contextReferenceId) return null;
  if (grant.contextType && grant.contextType !== owner.contextType) return null;
  return { token, grant, expiresAt: new Date(grant.exp * 1000) };
}

export function isOfflineChartRoute(pathname = window.location.pathname) {
  return /^\/charting\/[^/]+(?:\/|$)/.test(pathname);
}

function routeClient() {
  try { return decodeURIComponent(window.location.pathname.match(/^\/charting\/([^/]+)/)?.[1] || ''); }
  catch { return ''; }
}

function continuities() {
  try { const entries = JSON.parse(localStorage.getItem(CONTINUITY_KEY) || '[]'); return Array.isArray(entries) ? entries : []; }
  catch { return []; }
}

export function getActiveLaborContinuities() {
  const owner = getOfflineOwnerIdentity();
  return owner ? continuities().filter(item => item.ownerKey === owner.ownerKey) : [];
}

export function hasActiveLaborContinuity(context = {}) {
  const client = context.clientReferenceId || routeClient();
  if (!client) return false;
  return getActiveLaborContinuities().some(item => item.clientReferenceId === String(client)
    && (!context.pregnancyReferenceId || item.pregnancyReferenceId === String(context.pregnancyReferenceId))
    && (!context.episodeReferenceId || item.episodeReferenceId === String(context.episodeReferenceId)));
}

export function beginActiveLaborContinuity(context) {
  const owner = getOfflineOwnerIdentity();
  if (!owner || !context.clientReferenceId || !context.pregnancyReferenceId || !context.episodeReferenceId) return false;
  if (hasActiveLaborContinuity(context)) return true;
  const grant = getOfflineSession();
  if (owner.expiresAt <= Date.now() && !(grant && grant.expiresAt.getTime() > Date.now())) return false;
  const item = { ownerKey: owner.ownerKey, clientReferenceId: String(context.clientReferenceId),
    pregnancyReferenceId: String(context.pregnancyReferenceId), episodeReferenceId: String(context.episodeReferenceId), openedAt: Date.now() };
  localStorage.setItem(CONTINUITY_KEY, JSON.stringify([...continuities(), item]));
  setPreparedChartPin(item, `labor:${item.episodeReferenceId}`).catch(() => {});
  window.dispatchEvent(new CustomEvent('vela:offline-session-changed'));
  return true;
}

export function endActiveLaborContinuity({ episodeReferenceId }) {
  const owner = getOfflineOwnerIdentity();
  if (!owner) return;
  const removed = getActiveLaborContinuities().filter(item => item.episodeReferenceId === String(episodeReferenceId));
  localStorage.setItem(CONTINUITY_KEY, JSON.stringify(continuities().filter(item => !(item.ownerKey === owner.ownerKey && item.episodeReferenceId === String(episodeReferenceId)))));
  removed.forEach(item => setPreparedChartPin(item, `labor:${item.episodeReferenceId}`, false).catch(() => {}));
  window.dispatchEvent(new CustomEvent('vela:offline-session-changed'));
}

// This permits continuing only the already-open patient's local chart. It does
// not authenticate requests or permit opening other charts after grant expiry.
export function canContinueOfflineCharting(context = {}) {
  if (!isOfflineChartRoute() || !getOfflineOwnerIdentity()) return false;
  const client = context.clientReferenceId || routeClient();
  if (client !== routeClient()) return false;
  const session = getOfflineSession();
  return Boolean((session && session.expiresAt.getTime() > Date.now()) || hasActiveLaborContinuity({ ...context, clientReferenceId: client }));
}

// The roster is a navigation surface only. Resource reads remain chart-route scoped.
export function canContinuePreparedOffline() {
  if (window.location.pathname.replace(/\/$/, '') !== '/midwife/clients') return canContinueOfflineCharting();
  if (!getOfflineOwnerIdentity()) return false;
  const session = getOfflineSession();
  return Boolean((session && session.expiresAt.getTime() > Date.now()) || getActiveLaborContinuities().length);
}

export function shouldReadPreparedDirectly() {
  const owner = getOfflineOwnerIdentity();
  return Boolean(owner && owner.expiresAt <= Date.now() && canContinueOfflineCharting());
}

export async function prepareOfflineSession(hours = 24) {
  const owner = getOfflineOwnerIdentity();
  if (!owner || owner.expiresAt <= Date.now() || !navigator.onLine) throw new Error('Connect and sign in before preparing offline charting.');
  const response = await fetch(`${config.api.authUrl}/offline-session`, {
    method: 'POST', headers: { Authorization: `Bearer ${owner.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ requestedHours: hours }),
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error('Your session expired. Sign in again before preparing offline charting.');
    if (response.status === 403) throw new Error('Your account is not permitted to prepare offline charting.');
    throw new Error(`Could not prepare offline charting (HTTP ${response.status}).`);
  }
  const data = await response.json();
  if (getOfflineOwnerIdentity()?.ownerKey !== owner.ownerKey) throw new Error('Practice access changed during offline preparation.');
  localStorage.setItem(STORAGE_KEY, data.grant);
  const session = getOfflineSession();
  if (!session) { localStorage.removeItem(STORAGE_KEY); throw new Error('The offline access grant did not match the current account and practice.'); }
  window.dispatchEvent(new CustomEvent('vela:offline-session-changed'));
  return session;
}

export async function cacheOfflineChartData(urls, context = {}, { prefetched = [] } = {}) {
  const uniqueUrls = [...new Set((urls || []).filter(Boolean))];
  if (!uniqueUrls.length) throw new Error('Open a client pregnancy chart before preparing offline access.');
  if (!navigator.serviceWorker?.controller) throw new Error('Offline storage is not active yet. Refresh Sena while online and reopen this chart.');
  const owner = getOfflineOwnerIdentity();
  if (!owner || owner.expiresAt <= Date.now()) throw new Error('Sign in before preparing chart data.');
  const clientReferenceId = context.clientReferenceId || routeClient();
  await requireChartOfflineEligibility(clientReferenceId);
  const resources = await Promise.all(uniqueUrls.map(async url => {
    const alreadyFetched = prefetched.find(resource => resource.url === url);
    if (alreadyFetched) return alreadyFetched;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${owner.token}`, 'X-Sena-Offline-Prepare': 'true' }, cache: 'no-store' });
    if (!response.ok || response.headers.get('X-Sena-Prepared-Offline') === 'true') throw new Error(`Could not prepare a required chart resource (HTTP ${response.status}). Please retry while online.`);
    return { url, status: response.status, headers: { 'Content-Type': response.headers.get('Content-Type') || 'application/json' }, body: await response.text() };
  }));
  const session = getOfflineSession();
  if (!session || session.expiresAt.getTime() <= Date.now()) throw new Error('Prepare an offline access grant before saving this chart.');
  const activeEpisodes = getActiveLaborContinuities().filter(item => item.clientReferenceId === String(clientReferenceId)
    && item.pregnancyReferenceId === String(context.pregnancyReferenceId));
  const manifest = await storePreparedChartResources(resources, { ...context, clientReferenceId,
    pins: activeEpisodes.map(item => `labor:${item.episodeReferenceId}`) }, { owner, expiresAt: session.expiresAt.getTime() });
  window.caches?.delete?.('api-cache').catch(() => {});
  for (const active of getActiveLaborContinuities().filter(item => item.clientReferenceId === String(clientReferenceId)
    && item.pregnancyReferenceId === String(context.pregnancyReferenceId))) {
    await setPreparedChartPin(active, `labor:${active.episodeReferenceId}`);
  }
  return { resourceCount: manifest.resourceIds.length, manifest };
}

export async function readPreparedChartResponse(input, init = {}) {
  const method = String(init.method || input?.method || 'GET').toUpperCase();
  if (method !== 'GET') return null;
  const headers = new Headers(init.headers || input?.headers || {});
  if (headers.get('X-Sena-Offline-Prepare') === 'true') return null;
  const owner = getOfflineOwnerIdentity();
  if (!owner || !canContinueOfflineCharting()) return null;
  const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
  return readPreparedResource(url, { owner, clientReferenceId: routeClient(), allowExpired: hasActiveLaborContinuity(),
    activePregnancyReferences: getActiveLaborContinuities().filter(item => item.clientReferenceId === routeClient()).map(item => item.pregnancyReferenceId) });
}

export async function getOfflineChartReadiness(context) {
  const eligibility = await readChartEligibility(context.clientReferenceId || routeClient());
  if (eligibility?.eligible !== true) return { ready: false, onlineOnly: eligibility?.eligible === false };
  const manifest = await getPreparedChartManifest(context);
  const active = hasActiveLaborContinuity(context);
  return { ready: Boolean(manifest && (manifest.expiresAt > Date.now() || active) && canContinueOfflineCharting(context)), manifest };
}

export async function isOfflineUnlockRequired() {
  const status = await getVaultStatus();
  return status.enrolled && !isOfflineVaultUnlocked();
}

export function clearOfflineSession({ logout = false, owner = getOfflineOwnerIdentity() } = {}) {
  localStorage.removeItem(STORAGE_KEY);
  if (logout && owner) {
    localStorage.setItem(CONTINUITY_KEY, JSON.stringify(continuities().filter(item => item.ownerKey !== owner.ownerKey)));
    cleanupPreparedCharts({ owner, purge: true, releaseActiveLabor: true }).catch(() => {});
    window.dispatchEvent(new CustomEvent('vela:offline-owner-logged-out', { detail: { owner } }));
    lockOfflineVault();
  }
  window.dispatchEvent(new CustomEvent('vela:offline-session-changed'));
}
